import { dateUtils } from '@/utils'

const defaultRecurrenceSettings = {
  recurrencePeriod: { recurrenceType: 'NO_REPEAT', period: 1, date: '20250101' },
  recurrenceEnd: { endType: 'NEVER' },
} as const

export function computeInitialRecurrenceSettings(
  extOrder: uui.domain.client.rm.ExtendedOrderStep,
  recurrences: Record<string, uui.domain.client.rm.Recurrence>,
  today: Date,
): uui.domain.ui.forms.RecurrenceSettings {
  const recurrenceId = extOrder.order.recurrenceId

  const recurrence = recurrenceId ? recurrences[recurrenceId] : undefined
  if (recurrenceId && !recurrence) {
    throw new Error('Recurrence not found for the given order')
  }

  if (!recurrence) {
    return defaultRecurrenceSettings
  }

  const eligibility =
    extOrder.order.eligibility.type === 'on' ? extOrder.order.eligibility.dates[0] : undefined

  return {
    recurrencePeriod: computeRecurrencePeriodFromRecurrence(recurrence, today, eligibility),
    recurrenceEnd: computeRecurrenceEndFromRecurrence(recurrence),
  }
}

function computeRecurrencePeriodFromRecurrence(
  recurrence: uui.domain.client.rm.Recurrence,
  today: Date,
  eligibility?: string,
): uui.domain.ui.forms.RecurrencePeriod {
  const { recurrencePeriod } = recurrence
  const date = eligibility ? eligibility : dateUtils.convertToYYYYMMDD(today)

  switch (recurrencePeriod.recurrenceType) {
    case 'DAILY': {
      return {
        recurrenceType: 'DAILY',
        period: 1,
        date,
      }
    }

    case 'WEEKLY': {
      const weekday = dateUtils.toClientWeekday(recurrencePeriod.weekday)

      return {
        recurrenceType: 'WEEKLY',
        period: recurrencePeriod.period,
        weekday,
        date,
      }
    }

    case 'MONTHLY': {
      switch (recurrencePeriod.when) {
        case 'ON_DATE': {
          return {
            recurrenceType: 'MONTHLY',
            period: recurrencePeriod.period,
            date: recurrencePeriod.date,
          }
        }

        case 'ON_CARDINAL': {
          const weekday = dateUtils.toClientWeekday(recurrencePeriod.weekday)

          return {
            recurrenceType: 'MONTHLY',
            period: recurrencePeriod.period,
            cardinal: recurrencePeriod.cardinal,
            weekday,
            date: dateUtils.convertToYYYYMMDD(
              dateUtils.parseDateFromCardinality(recurrencePeriod.cardinal, weekday),
            ),
          }
        }
      }
    }

    case 'ANNUALLY': {
      switch (recurrencePeriod.when) {
        case 'ON_DATE': {
          return {
            recurrenceType: 'ANNUALLY',
            period: recurrencePeriod.period,
            date: recurrencePeriod.date,
          }
        }

        case 'ON_CARDINAL': {
          const weekday = dateUtils.toClientWeekday(recurrencePeriod.weekday)

          return {
            recurrenceType: 'ANNUALLY',
            period: recurrencePeriod.period,
            cardinal: recurrencePeriod.cardinal,
            weekday,
            month: recurrencePeriod.month,
            date: dateUtils.convertToYYYYMMDD(
              dateUtils.parseDateFromCardinality(
                recurrencePeriod.cardinal,
                weekday,
                recurrencePeriod.month,
              ),
            ),
          }
        }
      }
    }
  }
}

function computeRecurrenceEndFromRecurrence(
  recurrence: uui.domain.client.rm.Recurrence,
): uui.domain.client.rm.RecurrenceEnd {
  switch (recurrence.recurrenceEndType) {
    case 'NEVER':
      return { endType: 'NEVER' }

    case 'ON_DATE':
      if (!recurrence.endDate) {
        throw new Error('Recurrence end date must be defined for ON_DATE end type')
      }
      return { endType: 'ON_DATE', endDate: recurrence.endDate }

    case 'AFTER_NUMBER':
      if (typeof recurrence.requiredRecurrences !== 'number') {
        throw new Error(
          'Recurrence required recurrences must be a number for AFTER_NUMBER end type',
        )
      }
      return { endType: 'AFTER_NUMBER', numOfRecurrencesRequired: recurrence.requiredRecurrences }
  }
}
